import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, AlertTriangle } from 'lucide-react';
import { ConfirmBar } from '@ooh/ui/components/confirm-bar';
import { InlineErrorMessage } from '@ooh/ui/components/inline-error-message';
import type { SelectOption } from '@ooh/ui/components/select';
import { CONFIDENCE_THRESHOLD, type AvailabilityImportPreviewResponse } from '@ooh/contracts';
import { formatTemplate } from '@ooh/ui/lib/utils';
import { useConfirmAvailabilityImport } from '../api/mutations';
import { buildColumnMappingRows } from '../lib/build-column-mapping-rows';
import { buildTimeEncodingHelpSections } from '../lib/build-time-encoding-help-sections';
import { MANUALLY_SELECTABLE_ENCODINGS } from '../lib/manual-encodings';
import {
  type ConfirmGatingReasons,
  resolveDisabledReason,
  hasIncompleteRequiredField,
  resolveLastImportedAtLabel,
} from '../lib/preview-confirm-gating';
import { usePreviewProfileEditor } from '../hooks/use-preview-profile-editor';
import { usePreviewDisplayRows } from '../hooks/use-preview-display-rows';
import { ResolvedProfileCard } from './resolved-profile-card';
import { SupplierMappingFields } from './supplier-mapping-fields';
import { ColumnMappingTable } from './column-mapping-table';
import { InterpretedRowsTable } from './interpreted-rows-table';
import { TimeEncodingSelector } from './time-encoding-selector';
import { TimeEncodingHelpDialog } from './time-encoding-help-dialog';
import { RememberedMappingBanner } from './remembered-mapping-banner';
import { UnmatchedMappingWarning } from './unmatched-mapping-warning';
import {
  DateRangeSection,
  MonthPeriodSection,
  YearSpanWarningSection,
  GlobalRangeSection,
  PeriodLabelSection,
  MarkRestSection,
  StatusTokenSection,
} from './preview-phase-sections';

export interface PreviewPhaseEditorProps {
  jobId: number;
  initialData: AvailabilityImportPreviewResponse;
  realSupplierOptions: SelectOption[];
  /** Previously-known `defaultSupplierName` to re-apply post-mount after a
   * "Wyczyść mapowanie" reset — see `resolveCarryOverSupplierName`'s
   * docstring (CU-86cbafj4j). `undefined` on the initial (non-reset) mount. */
  carryOverSupplierName?: string;
  onReset: () => void;
  resetting: boolean;
  onBack: () => void;
  onConfirmed: () => void;
}

/** `initialData.columnDistinctValues` defaults to `{}` (optional field, see
 * `AvailabilityImportPreviewResponseSchema`) — extracted to a top-level
 * function so the `??` branch doesn't count toward `PreviewPhaseEditor`'s
 * own cyclomatic-complexity budget. */
function resolveColumnDistinctValues(
  initialData: AvailabilityImportPreviewResponse,
): Record<string, string[]> {
  return initialData.columnDistinctValues ?? {};
}

export function PreviewPhaseEditor({
  jobId,
  initialData,
  realSupplierOptions,
  carryOverSupplierName,
  onReset,
  resetting,
  onBack,
  onConfirmed,
}: PreviewPhaseEditorProps) {
  const { t } = useTranslation('availabilityImport');
  // Sourced from the FRESH preview response (parsed against whichever
  // header row was actually used), never a stale upload-time snapshot — see
  // `AvailabilityImportPreviewResponseSchema`'s doc comment (QA bugfix
  // round 2, CU-86cb1npz2).
  const sampleRows = initialData.sampleRows;
  const columnDistinctValues = resolveColumnDistinctValues(initialData);
  const editor = usePreviewProfileEditor(
    initialData.profile,
    sampleRows,
    realSupplierOptions,
    columnDistinctValues,
  );
  const { profile } = editor;
  const carryOverAppliedRef = React.useRef(false);
  React.useEffect(() => {
    if (carryOverAppliedRef.current) return;
    carryOverAppliedRef.current = true;
    if (carryOverSupplierName) {
      // Applied through the SAME code path a manual "Dostawca" pick takes
      // (not baked into the initial profile state) so `useRematchedRows`'
      // debounced-rematch fingerprint — captured at mount from the RAW
      // (null-supplier) profile — sees a real post-mount change and fires
      // `POST /rematch` to reconcile against the server's actually-
      // unmatched rows, exactly like a manual edit would (CU-86cbafj4j).
      editor.setSupplierName(carryOverSupplierName);
    }
    // Fires once per mount (guarded by the ref): the component fully remounts per reset via `key={jobId}-${resetGeneration}` in PreviewPhase.
  }, [carryOverSupplierName, editor]);
  const confirmMutation = useConfirmAvailabilityImport(jobId, onConfirmed);

  // Built from the FRESH preview response's own `headers` (reflects the
  // operator-confirmed headerRowIndex), never from the upload response's
  // `headers` — that field is computed once at upload time against
  // detectHeaderRow's auto-detected row and goes stale after a header-row
  // override (QA bugfix round 1, CU-86cb1npz2).
  const columnOptions: SelectOption[] = initialData.headers.map((h) => ({ value: h, label: h }));
  const columnMappingRows = buildColumnMappingRows(
    profile,
    sampleRows,
    {
      supplierNumberFieldLabel: t('preview.supplierNumberColumnLabel'),
      addressFieldLabel: t('preview.addressFieldLabel'),
      cityFieldLabel: t('preview.cityFieldLabel'),
      statusFieldLabel: t('preview.statusColumnLabel'),
      dateFromFieldLabel: t('preview.dateFromColumnLabel'),
      dateToFieldLabel: t('preview.dateToColumnLabel'),
    },
    {
      onSupplierNumberColumnChange: editor.handleSupplierNumberColumnChange,
      onAddressColumnChange: editor.handleAddressColumnChange,
      onCityColumnChange: editor.handleCityColumnChange,
      onStatusColumnChange: editor.handleStatusColumnChange,
      onDateRangeColumnChange: editor.handleDateRangeColumnChange,
    },
  );
  const { displayRows, allRowsUnmatched, rematchFailed } = usePreviewDisplayRows(
    jobId,
    initialData,
    profile,
  );
  const gatingReasons: ConfirmGatingReasons = { ...editor, allRowsUnmatched };

  const confirmDisabled = confirmMutation.isPending || hasIncompleteRequiredField(gatingReasons);
  const disabledReason = resolveDisabledReason(t, gatingReasons);

  function handleConfirm() {
    confirmMutation.mutate({
      profile,
      markRestUnavailable: editor.showMarkRestUnavailable ? editor.markRestUnavailable : false,
    });
  }

  const isConfident = initialData.confidence >= CONFIDENCE_THRESHOLD;
  const isRememberedMapping = initialData.mappingSource === 'remembered';
  const timeEncodingHelpSections = buildTimeEncodingHelpSections(t);

  /** Label for the next manually-added period row (CU-86cb1npz2, gap #2) —
   * e.g. "Okres 3" — computed from the current row count since a manually-
   * built row has no auto-detected header text to reuse (see
   * `useMonthPeriodEditor`'s `addPeriodRow`). Uses `formatTemplate` (not
   * i18next's own `t(key, { count })` interpolation) because this
   * project's locale strings use single-brace `{count}` templates —
   * i18next's `count` option is reserved for pluralization lookups
   * (`key_one`/`key_other`) and does not substitute it (QA bugfix round 1,
   * CU-86cb1npz2). */
  function nextManualPeriodLabel(): string {
    return formatTemplate(t('preview.monthPeriodManualRowLabelTemplate'), {
      count: editor.monthRows.length + 1,
    });
  }

  return (
    <div className="space-y-6" data-testid="phase-preview">
      {isRememberedMapping ? (
        <RememberedMappingBanner
          labels={{
            heading: t('preview.rememberedMappingHeading'),
            dateTemplate: t('preview.rememberedMappingDateTemplate'),
            noDateFallback: t('preview.rememberedMappingNoDateFallback'),
            resetActionLabel: t('preview.rememberedMappingResetLabel'),
          }}
          lastImportedAtLabel={resolveLastImportedAtLabel(initialData.mappingSavedAt)}
          onReset={onReset}
          resetting={resetting}
        />
      ) : null}
      <ResolvedProfileCard
        heading={t('preview.heading')}
        helperText={t('preview.helperText')}
        badgeTone={isConfident ? 'success' : 'warning'}
        badgeIcon={isConfident ? ShieldCheck : AlertTriangle}
        badgeText={isConfident ? t('preview.confidentBadge') : t('preview.lowConfidenceBadge')}
      >
        <div className="space-y-4">
          <TimeEncodingSelector
            value={profile.timeEncoding}
            canOverride={!isConfident && editor.canOverrideTimeEncoding}
            onChange={editor.setTimeEncoding}
            encodingOptions={MANUALLY_SELECTABLE_ENCODINGS}
            labels={{
              label: t('preview.timeEncodingLabel'),
              options: {
                global: t('preview.timeEncodingOptions.global'),
                date_range: t('preview.timeEncodingOptions.date_range'),
                period_label_map: t('preview.timeEncodingOptions.period_label_map'),
                month: t('preview.timeEncodingOptions.month'),
              },
            }}
            labelSlot={
              <TimeEncodingHelpDialog
                labels={{
                  triggerLabel: t('preview.timeEncodingHelp.triggerLabel'),
                  dialogTitle: t('preview.timeEncodingHelp.dialogTitle'),
                  sections: timeEncodingHelpSections,
                }}
                encodingOptions={MANUALLY_SELECTABLE_ENCODINGS}
                testIdPrefix="preview-time-encoding-help"
              />
            }
          />
          <div className="grid grid-cols-2 gap-4">
            <SupplierMappingFields
              supplierOptions={editor.supplierOptions}
              supplier={editor.supplierName}
              onSupplierChange={editor.setSupplierName}
              labels={{
                supplierLabel: t('preview.supplierLabel'),
                supplierPlaceholder: t('preview.supplierPlaceholder'),
                clearOptionLabel: t('preview.columnMappingClearOption'),
              }}
              error={editor.supplierIncomplete}
              testIdPrefix="preview"
            />
          </div>
          <ColumnMappingTable
            rows={columnMappingRows}
            columnOptions={columnOptions}
            labels={{
              systemFieldHeader: t('preview.columnMappingTableSystemFieldHeader'),
              fileColumnHeader: t('preview.columnMappingTableFileColumnHeader'),
              sampleValueHeader: t('preview.columnMappingTableSampleValueHeader'),
              columnPlaceholder: t('preview.columnPlaceholder'),
              sampleEmptyPlaceholder: t('preview.columnMappingTableSampleEmptyPlaceholder'),
              optionalTag: t('preview.columnMappingOptionalTag'),
              clearOptionLabel: t('preview.columnMappingClearOption'),
            }}
            testIdPrefix="preview-column-mapping"
          />
          <DateRangeSection
            t={t}
            profile={profile}
            dateFormatSelection={editor.dateFormatSelection}
            onDateFormatChange={editor.handleDateFormatChange}
            dateFormatCustomValue={editor.dateFormatCustomValue}
            onDateFormatCustomChange={editor.handleDateFormatCustomChange}
          />

          <MonthPeriodSection
            t={t}
            profile={profile}
            columnOptions={columnOptions}
            rows={editor.monthRows}
            onUpdateRow={editor.updatePeriodRow}
            onUpdateYear={editor.updatePeriodYear}
            isConfident={isConfident}
            onAddRow={() => editor.addPeriodRow(nextManualPeriodLabel())}
            onRemoveRow={editor.removePeriodRow}
          />
          <YearSpanWarningSection t={t} profile={profile} />
          <StatusTokenSection
            t={t}
            tokens={editor.unmappedStatusTokens}
            statusDictionary={profile.statusDictionary}
            onUpdate={editor.updateStatusDictionaryEntry}
          />
          <GlobalRangeSection
            t={t}
            profile={profile}
            dateFrom={editor.globalDateFrom}
            dateTo={editor.globalDateTo}
            onChange={editor.handleGlobalRangeChange}
          />
          <PeriodLabelSection
            t={t}
            profile={profile}
            columnOptions={columnOptions}
            onColumnChange={editor.handlePeriodLabelColumnChange}
            mappings={editor.periodLabelMappings}
            onUpdate={editor.updatePeriodLabelMapping}
          />
          <MarkRestSection
            t={t}
            show={editor.showMarkRestUnavailable}
            checked={editor.markRestUnavailable}
            onCheckedChange={editor.setMarkRestUnavailable}
            supplierName={editor.supplierName}
            count={sampleRows.length}
          />
        </div>
      </ResolvedProfileCard>

      <InterpretedRowsTable
        rows={displayRows}
        labels={{
          unitColumnLabel: t('preview.rowsUnitColumnLabel'),
          periodColumnLabel: t('preview.rowsPeriodColumnLabel'),
          statusColumnLabel: t('preview.rowsStatusColumnLabel'),
          unmatchedLabelTemplate: t('preview.rowsUnmatchedTemplate'),
          unmatchedTooltip: t('preview.rowsUnmatchedTooltip'),
          emptyPlaceholder: t('preview.rowsEmptyPlaceholder'),
          statusLabels: {
            FREE: t('preview.rowsStatusValues.FREE'),
            UNAVAILABLE: t('preview.rowsStatusValues.UNAVAILABLE'),
          },
        }}
        unmatchedIcon={AlertTriangle}
        testId="preview-interpreted-rows"
      />

      {allRowsUnmatched ? (
        <UnmatchedMappingWarning labels={{ text: t('preview.unmatchedMappingWarningText') }} />
      ) : null}

      {rematchFailed ? (
        <InlineErrorMessage tone="warning" icon={AlertTriangle} testId="rematch-failed-warning">
          {t('preview.rematchFailedWarning')}
        </InlineErrorMessage>
      ) : null}

      <ConfirmBar
        backLabel={t('preview.back')}
        confirmLabel={confirmMutation.isPending ? t('preview.confirming') : t('preview.confirm')}
        onBack={onBack}
        onConfirm={handleConfirm}
        disabled={confirmDisabled}
        disabledReason={disabledReason}
        backTestId="back-to-upload"
        confirmTestId="confirm-preview"
        disabledReasonTestId="confirm-disabled-reason"
      />
    </div>
  );
}
